import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import validateField from "../utils/fieldValidator";
import { removeUnitsFromPreferences } from "../utils/transformPreferences";

function SimpleFormModal({
  isOpen,
  title,
  initialData,
  fields,
  onSave,
  onCancel,
  defaultValues,
  onFieldChange = null
}) {

  const isEditMode = initialData && initialData.id !== undefined;

  const getAsterisks = (fieldName) => {
    if (defaultValues && defaultValues[fieldName] !== undefined) {
      if (defaultValues[fieldName] === null) {
        return <span className="required-asterisks">**</span>;
      }
      return <span className="required-asterisks">*</span>;
    }
    return <span className="required-asterisks invisible">*</span>;
  };

  const getInitialFormState = useCallback(() => {
    const state = {};
    fields.forEach((field) => {
      if (isEditMode) {
        if (initialData && initialData[field.name] !== undefined) {
          if (field.name === "preferences" && typeof initialData[field.name] === "object" && initialData[field.name] !== null) {
            if (typeof field.transform === "function") {
              state[field.name] = field.transform(initialData[field.name]);
            } else {
              state[field.name] = JSON.stringify(initialData[field.name], null, 2);
            }
          } else if (initialData[field.name] === null) {
            state[field.name] = field.type === "select" ? null : "";
          } else {
            state[field.name] = initialData[field.name];
          }
        } else {
          state[field.name] = "";
        }
      } else {
        if (initialData && initialData[field.name] !== undefined) {
          state[field.name] = initialData[field.name];
        } else if (defaultValues && defaultValues[field.name] !== undefined) {
          state[field.name] =
            defaultValues[field.name] === null
              ? (field.type === "select" ? null : "")
              : defaultValues[field.name];
        } else {
          state[field.name] = "";
        }
      }
    });
    return state;
  }, [fields, initialData, defaultValues, isEditMode]);

  const [formData, setFormData] = useState(getInitialFormState());
  const [errors, setErrors] = useState({});
  const firstInputRef = useRef(null);
  const preferencesRef = useRef(null);
  const commentRef = useRef(null);
  const wasOpenRef = useRef(false);


  useEffect(() => {
    const justOpened = isOpen && !wasOpenRef.current;
    wasOpenRef.current = isOpen;
    if (!justOpened) return;   
    setFormData(getInitialFormState());
    setErrors({});
  }, [getInitialFormState, isOpen]);

  useEffect(() => {
    if (isOpen && formData.preferences) {
      const timer = setTimeout(() => {
        if (preferencesRef.current) { // This condition is defensive. In normal operation, preferencesRef.current should always be truthy, so the else branch is not expected to be executed.
          preferencesRef.current.style.height = "auto";
          preferencesRef.current.style.height = `${preferencesRef.current.scrollHeight}px`;
        }
      }, 100);
      return () => clearTimeout(timer);
    }

    if (isOpen && formData.comment) {
      const timer = setTimeout(() => {
        if (commentRef.current) { // This condition is defensive. In normal operation, commentRef.current should always be truthy, so the else branch is not expected to be executed.
          commentRef.current.style.height = "auto";
          commentRef.current.style.height = `${commentRef.current.scrollHeight}px`;
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isOpen, formData.preferences, formData.comment]);


function handleChange(e) {
  const { name } = e.target;

  let val = e.target.value;
  if ((name === "capacity_tariff_type" || name === "energy_tariff_type") && val === "") {
    val = null;
  }

  setFormData(prev => {
    const next = { ...prev, [name]: val };
    const patch = typeof onFieldChange === "function" ? onFieldChange(name, val, next) : null;
    return patch && typeof patch === "object" ? { ...next, ...patch } : next;
  });

  const errorMessage = validateField(name, val);
  setErrors(prev => ({ ...prev, [name]: errorMessage }));
}

  function handleSubmit() {
    const normalizedData = {};
    fields.forEach((field) => {
      const value = formData[field.name];
      if (field.name === "preferences") {
        try {
          let parsedJson = JSON.parse(value);
          parsedJson = removeUnitsFromPreferences(parsedJson);
          normalizedData[field.name] = parsedJson;
        } catch (e) {
          normalizedData[field.name] = value;
        }
      } else {
        if (value === null || value === undefined) {
          normalizedData[field.name] = null;
        } else {
          const trimmedValue = value.toString().trim();
          if (defaultValues && defaultValues[field.name] !== undefined) {
            if (defaultValues[field.name] === null) {
              normalizedData[field.name] = trimmedValue === "" ? null : value;
            } else {
              normalizedData[field.name] =
                trimmedValue === "" ? defaultValues[field.name] : value;
            }
          } else {
            normalizedData[field.name] = value;
          }
        }
      }
    });

    const newErrors = {};
    fields.forEach((field) => {
      let valueForValidation;
      if (field.type === "select") {
        valueForValidation = normalizedData[field.name];
      } else {
        valueForValidation =
          normalizedData[field.name] === null ? "" : normalizedData[field.name];
      }
      let errorMessage = validateField(field.name, valueForValidation);
      if (
        defaultValues &&
        defaultValues[field.name] === null &&
        errorMessage === "Field cannot be empty"
      ) {
        errorMessage = null;
      }
      if (errorMessage) {
        newErrors[field.name] = errorMessage;
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    const updatedData =
      isEditMode
        ? { ...normalizedData, id: initialData.id }
        : normalizedData;
    onSave(updatedData);
  }

  return (
    <Modal
      backdrop="static"
      keyboard={false}
      isOpen={isOpen}
      toggle={onCancel}
      onOpened={() => {
        if (firstInputRef.current) { // This condition is defensive. In normal operation, firstInputRef.current should always be truthy, so the else branch is not expected to be executed.
          setTimeout(() => {
            if (firstInputRef.current) {
              firstInputRef.current.focus();
            }
          }, 200);
        }
      }}
    >
      <ModalHeader
        toggle={onCancel}
        close={
          <FontAwesomeIcon
            color="black"
            icon="times"
            size="2x"
            onClick={onCancel}
          />
        }
      >
        {title}
      </ModalHeader>
      <ModalBody>
        <Form>
          {fields.map((field, index) => {
            const hasDefaultValue = defaultValues && defaultValues[field.name] !== undefined;
            const defaultValue = hasDefaultValue
              ? (defaultValues[field.name] === null ? "" : defaultValues[field.name])
              : "";
            const applyDefaultValueStyle = !isEditMode && hasDefaultValue && formData[field.name] === defaultValue;
            const inputClassName = `form-control ${applyDefaultValueStyle ? "default-value" : ""}`;

            if (field.type === "switch") {
              return (
                <FormGroup
                  key={field.name}
                  className="d-flex align-items-center justify-content-between"
                >
                  <Label for={field.name} className="mb-0">
                    {getAsterisks(field.name)} {field.label}
                  </Label>
                  <div>
                    <label className="switch mb-0">
                      <input
                        type="checkbox"
                        aria-label={field.label}
                        name={field.name}
                        id={field.name}
                        checked={!!formData[field.name]}
                        onChange={(e) => {
                          handleChange({
                            target: { name: field.name, value: e.target.checked }
                          });
                        }}
                      />
                      <span className="slider round"></span>
                    </label>
                  </div>
                  {errors[field.name] && (
                    <FormFeedback style={{ display: "block" }}>
                      {errors[field.name]}
                    </FormFeedback>
                  )}
                </FormGroup>
              );
            }

            return (
              <FormGroup key={field.name}>
                <Label for={field.name} style={{ display: "block" }}>
                  {getAsterisks(field.name)} {field.label}
                </Label>
                {field.options ? (
                  <Input
                    aria-label={field.label}
                    className="form-control"
                    type="select"
                    name={field.name}
                    id={field.name}
                    value={formData[field.name] === null ? "" : formData[field.name]}
                    onChange={handleChange}
                    invalid={!!errors[field.name]}
                  >
                    {(field.options).map((option) => (
                      <option key={option.value} value={option.value === null ? "" : option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                ) : field.name === "preferences" ? (
                  <textarea
                    aria-label={field.label}
                    className={inputClassName}
                    name={field.name}
                    id={field.name}
                    ref={preferencesRef}
                    value={
                      formData[field.name] &&
                        formData[field.name] !== undefined
                        ? formData[field.name]
                        : ""
                    }
                    onChange={(e) => {
                      handleChange(e);
                      e.target.style.height = "auto";
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    style={{
                      width: "100%",
                      overflow: "hidden",
                      resize: "vertical",
                    }}
                    rows={1}
                  />
                ) : field.name === "comment" ? (
                  <textarea
                    aria-label={field.label}
                    className={inputClassName}
                    name={field.name}
                    id={field.name}
                    ref={commentRef}
                    value={
                      formData[field.name] &&
                        formData[field.name] !== undefined
                        ? formData[field.name]
                        : ""
                    }
                    onChange={(e) => {
                      handleChange(e);
                      e.target.style.height = "auto";
                      e.target.style.height = `${e.target.scrollHeight}px`;
                    }}
                    style={{
                      width: "100%",
                      overflow: "hidden",
                      resize: "vertical",
                    }}
                    rows={1}
                  />
                ) : (
                  <Input
                    aria-label={field.label}
                    className={inputClassName}
                    innerRef={index === 0 ? firstInputRef : null}
                    type={field.type}
                    name={field.name}
                    id={field.name}
                    value={formData[field.name] !== undefined ? formData[field.name] : ""}
                    onChange={handleChange}
                    invalid={!!errors[field.name]}
                  />
                )}
                {errors[field.name] && (
                  <FormFeedback style={field.options ? { display: "block" } : {}}>{errors[field.name]}</FormFeedback>
                )}
              </FormGroup>
            );
          })}

        </Form>
      </ModalBody>
      <ModalFooter>
        <div className="w-100">
          <div className="asterisk-help">
            <div>* If empty, the default value will be used</div>
            <div>** Can be empty</div>
          </div>
          <div className="mt-2 d-flex justify-content-end">
            <Button color="secondary" onClick={onCancel} style={{ marginRight: "1rem" }}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleSubmit}>
              OK
            </Button>
          </div>
        </div>
      </ModalFooter>
    </Modal>
  );
}

export default SimpleFormModal;